<template>
	<article :id="'question-' + activeQuestion.TEST_QUESTION_RID" class="question-display">
		<div class="sr-only">
			<p>
				{{ $t('MAIN_CONTENT.testTaker.questionNumber', { number: activeQuestion.index + 1 } ) }}
			</p>
		</div>

		<div class="pull-right">
			<span class="pill pill-dark">
				{{ $t('MAIN_CONTENT.testTaker.pointsPossible', { points: activeQuestion.TOTAL_POINTS } ) }}
			</span>
		</div>

		<div class="question-type-instructions">
			<strong>
				{{ activeQuestion.QUESTION_NAME }}
			</strong>
			<span class="hint">
				{{ $t('MAIN_CONTENT.trueFalseQuestion.questionMessage') }}
			</span>
		</div>

		<h3 class="question-text" v-html="activeQuestion.QUESTION_TEXT" />

		<div class="answers">
			<!-- note the double event handlers - one for click and one for keyboard -->
			<div v-for="( answer, index ) in trueFalseDefaults" 
						:key="index" 
						:class="getAnswerClass( answer )"
						tabindex="0"
						@click="answerTheQuestion( answer )"
						@keyup.enter="answerTheQuestion( answer )">
				<div :class="[ 'answer-number',{ selected:answeredByStudent( answer ) } ]">
					<i v-if="answeredByStudent( answer )"
							class="fa fas fa-arrow-circle-right"
							:aria-label="'Selected Answer ' + answeredByStudent( answer )"
							aria-live="polite" />
					{{ answer.ANSWER_NUMBER }}
					<div v-if="isGraded && answeredByStudent( answer )"
								class="answer-points"
								aria-label="Points Earned">
						{{ $t('MAIN_CONTENT.testTaker.points', { points: pointsEarned } ) }}
					</div>
				</div>
				<div class="answer-text">
						{{ answer.ANSWER_TEXT }}
				</div>
				<div class="grade-result">
					<i :class="getFeedbackIcon( answer )" />
				</div>
			</div>
		</div>

		<question-summary />

		<test-buttons v-if="testMode !== 'grading'" @answer-save="submitAnswer" />
	</article>
</template>

<script>
	import { mapState,mapActions,mapMutations } from "vuex";
	import testButtons from "./testButtons.vue";
	import questionSummary from "./questionSummary.vue";

	export default {
			components : { testButtons, questionSummary },
			props      : {
					testPreview: {
							type     : Object,
							required : true
					},
					activeQuestion: {
							type     : Object,
							required : true
					},
					studentAnswer: {
							type    : Array,
							default : function(){
									return {};
							}
					},
					testMode: {
							type    : String,
							default : "student"
					}
			},
			data (){
					return{
							answerData: {
									questionId : "",
									answer     : []
							},
							trueFalseDefaults: [
									{
											ANSWER_CORRECT_FLG : "",
											ANSWER_TEXT        : "True",
											ANSWER_NUMBER      : "T",
											ANSWER_BOOL        : true
									},
									{
											ANSWER_CORRECT_FLG : "",
											ANSWER_TEXT        : "False",
											ANSWER_NUMBER      : "F",
											ANSWER_BOOL        : false
									}
							]
					};
			},
			computed: {
					...mapState( {
							studentAnswers: state =>{
									return state.testTaker.testPreview.STUDENT_ATTEMPT.ANSWERS;
							},
							blankAnswer: state =>{
									return state.testTaker.blankAnswerObj;
							}
					} ),
					allowChanges (){
							return this.testPreview.TEST.ALLOW_ANSWER_CHANGE;
					},
					activeQuestionAnswers (){
							return this.testPreview.ANSWERS.filter( ans =>{
									return ans.TEST_QUESTION_RID === this.activeQuestion.TEST_QUESTION_RID; 
							} );
					},
					answerChoice (){
							return this.studentAnswer && this.studentAnswer.length ? this.studentAnswer[0] : {};
					},
					pointsEarned (){
							let total = 0;
							this.studentAnswer.forEach( ans =>{
									total += ans.POINTS_EARNED;
							} );
							return total;
					},
					// returns true if question has been graded
					isGraded (){
							return this.testMode === "grading" || (this.studentAnswer.filter( ans =>{
									return ans.QUESTION_GRADED_STATUS; 
							} ).length > 0);
					},
			},
			methods: {
					...mapMutations( [ "updateState","setUnsaved" ] ),
					...mapActions( [ "update","scoreAnswer","postAnswer" ] ),

					convertToText ( answer ){
							return answer ? "True" : "False";
					},
					convertToBoolean ( answer ){
							let result = "";
							if(typeof( answer ) === "number"){
									let answerObj = this.testPreview.ANSWERS.find(itm=>{
											return itm.ANSWER_RID === answer
									});
									if(answerObj){
											answer = answerObj.ANSWER_TEXT;
									}
							}
							if( typeof( answer ) === "string" ){
									result = answer.toLowerCase() === "true" ? true : false; 
							}
							if( typeof( answer ) === "boolean" ){
									result = answer;
							}
							return result;
					},
					// will return css class for check if correct and x for incorrect for graded answers
					getFeedbackIcon ( answer ){
							if( !this.isGraded ) return;
							return this.isCorrect( answer ) ? "fa fas fa-check-circle" :"fa fas fa-times-circle";
					},
					// returns css class if graded correct
					getAnswerClass ( answer ){
							let cls = "answer";
							if( !this.isGraded ){
									cls += " clickable";
							}else{
									cls += this.isCorrect( answer ) ? " correct" : " incorrect";
							}
							return cls;
					},
					// compares default answers against corrected answer key
					isCorrect ( answer ){
							if( ! this.isGraded ) return;
							return answer.ANSWER_BOOL === this.convertToBoolean( this.answerChoice.TEST_QUESTION_ANSWER_TEXT ) && this.answerChoice.ANSWER_CORRECT_FLG ||
							answer.ANSWER_BOOL !== this.convertToBoolean( this.answerChoice.TEST_QUESTION_ANSWER_TEXT ) && !this.answerChoice.ANSWER_CORRECT_FLG;
					},
					// did the student answer the question
					answeredByStudent ( answer ){
							if( !this.studentAnswer.length || !this.studentAnswer[ 0 ].hasOwnProperty("TEST_QUESTION_ANSWER_TEXT") || !this.studentAnswer[ 0 ].TEST_QUESTION_ANSWER_TEXT.toString().length ) return false;
							return this.convertToBoolean( this.studentAnswer[ 0 ].TEST_QUESTION_ANSWER_TEXT ) === this.convertToBoolean(answer.ANSWER_TEXT);
					},
					// returns either letter, number or roman numeral for the answer choice
					getAnswerChoice ( index ){
							return this.TF[ index ];
					},
					getAnswerChoiceNumber ( answerId ){
							let index = this.answerChoices.map( ans => {
									return ans.ANSWER_RID; 
							} ).indexOf( answerId );
							return this.getAnswerChoice( index );
					},
					prepareAnswer ( answer ){
							// prepare payload with activeQuestion id to use as a key
							this.$set(this.answerData, "questionId" , this.activeQuestion.TEST_QUESTION_RID);
							this.$set(this.answerData, "testId", this.activeQuestion.TEST_RID);
							this.$set(this.answerData, "questionType", this.activeQuestion.QUESTION_TYPE);
							// clone a copy of the blankAnswer
							let tmpAnswer = [];
							tmpAnswer.push(JSON.parse( JSON.stringify( this.blankAnswer ) ));
							// populate it
							tmpAnswer[0].TEST_RID = this.activeQuestion.TEST_RID;
							tmpAnswer[0].TEST_QUESTION_RID = this.activeQuestion.TEST_QUESTION_RID;
							tmpAnswer[0].TEST_QUESTION_ANSWER_TEXT = answer.ANSWER_TEXT;
							this.$set(this.answerData, "answer", tmpAnswer);

					},
					answerTheQuestion ( answer ){
							// question has been answered, so no changes
							if( this.isGraded && !this.allowChanges ) return;
							this.prepareAnswer( answer );

							// tracking unsaved responses
							this.setUnsaved( { key: this.activeQuestion.TEST_QUESTION_RID, value: JSON.parse( JSON.stringify(this.answerData)) } );
							// save the answer

							this.postAnswer( { 
											"key": this.answerData.questionId,
											"data": JSON.stringify(this.answerData),
											"queryParams":{},
											"routeParams":{ 
													action: "answer-question", 
													testRid: this.$route.params.testRid
											} 
									}
							).then(()=>{
									// clear this answer from unsaved
									this.setUnsaved( { key: this.activeQuestion.TEST_QUESTION_RID, value: "" } );
							});
					},
					submitAnswer (){
							if( !this.answerData.questionId || (!this.studentAnswer.length || !this.studentAnswer[ 0 ].hasOwnProperty("TEST_QUESTION_ANSWER_TEXT") || !this.studentAnswer[ 0 ].TEST_QUESTION_ANSWER_TEXT.toString().length)) return;
							// clear this answer from unsaved
							this.setUnsaved( { key: this.answerData.questionId, value: "" } );

							this.scoreAnswer( { 
											"key": this.answerData.questionId,
											"data": JSON.stringify(this.answerData),
											"queryParams":{},
											"routeParams":{ 
													action: "score-question", 
													testRid: this.$route.params.testRid
											} 
									}
							);
					},
					populateForm (){
							// start with the blank answer obj
							this.$set(this.answerData, "questionId" , this.activeQuestion.TEST_QUESTION_RID);
							this.$set(this.answerData, "testId", this.activeQuestion.TEST_RID);
							this.$set(this.answerData, "answer", JSON.parse( JSON.stringify( this.blankAnswer ) ));
							if( this.studentAnswer ){
									this.answerData.answer = this.studentAnswer;
							}
					}
			},
			watch: {
					activeQuestion: {
							immediate: true,
							handler: function(newValue, oldValue) {
									this.populateForm();
							},
					},
			},
			created (){
					this.populateForm();
			},
			mounted (){
					try{
							document.getElementById( "testZone" ).focus();
					}catch(e){}
			}
	};
</script>
